// Which Umami site each app reports to, resolved at runtime from the hostname.
//
// ── Why this is a table and not a build arg ──────────────────────────────────────────────────────
// This monorepo has NO `VITE_` build args, deliberately. `frontend/Dockerfile` builds all the apps
// in one cached layer and selects the output per image; a per-app or per-environment arg would fork
// that cache and put six `npm ci` + six Vite builds back into every deploy. The docs app solved the
// same problem the same way — it derives its host at runtime rather than being told at build time.
//
// Nothing here is a secret. The Umami URL and website id are handed to the browser in the script
// tag on every page load; they identify a site, they do not authorise anything.
//
// ── Why the environment is part of it ────────────────────────────────────────────────────────────
// Staging and production run the SAME image. Without a per-environment id, every click made while
// testing on staging would land in the production numbers, and the first question anyone asks of an
// analytics dashboard — "is this real?" — could never be answered yes.

import type { UmamiConfig } from './analytics';

export type AnalyticsApp = 'public' | 'clinic' | 'patient' | 'supplier' | 'imaging' | 'docs';

/** The self-hosted instance. One server for every product; sites are separated by website id. */
const UMAMI_URL = 'https://analytics.easysoft.ro';

/**
 * Website ids, from Umami (CT 116 on esy-ops).
 *
 * Not secrets — each is handed to the browser in the script tag on every page load. They name a
 * site so hits can be attributed to it; they authorise nothing.
 *
 * A blank id turns tracking off for that app rather than pointing it at nothing. The production ids
 * exist and are correct, but easyDent has no production deployment yet, so those sites simply sit
 * at zero until it does — nothing to remember on cutover day.
 */
const WEBSITE_IDS: Record<'staging' | 'production', Record<AnalyticsApp, string>> = {
  staging: {
    public: '95ff7c7b-f09e-4b61-b958-7a94f84792df',
    clinic: '1c67669e-d6fa-4ec5-95d4-474228a67f91',
    patient: '8f9625bf-3fd4-435d-806d-b7d4a7a8929c',
    supplier: '7049151c-e470-4da9-9dd4-99a59727b740',
    imaging: '36000446-0416-4875-9c20-3d59acf20780',
    docs: 'd7683f45-f0e9-4f30-8114-f3365ecb28f1',
  },
  production: {
    public: 'c8e650d1-eac2-452f-8bac-e3bf5a4c1b67',
    clinic: '5afaa997-e10e-45c1-ace7-302ac67249a7',
    patient: '10344530-4c6b-4473-bbf3-0d53eca6764e',
    supplier: 'd8b9509d-e663-43e4-8524-1cf4346dc535',
    imaging: '483a6a3d-ac56-4a83-9de2-444194536c3c',
    docs: '4052a19a-7cfc-4702-bc79-7df7bef2cff3',
  },
};

/**
 * Which deployment the page is being served from.
 *
 * Returns null for anything unrecognised — localhost, a preview build, an IP address, someone's
 * laptop. Development traffic is not data, and the safe default when we cannot tell is to collect
 * nothing rather than to guess and pollute a real site.
 */
function environmentFor(hostname: string): 'staging' | 'production' | null {
  // Development is not data. Anything without a real domain collects nothing.
  if (hostname === 'localhost' || hostname === '127.0.0.1' || /^[\d.]+$/.test(hostname)) return null;
  if (!hostname.includes('.')) return null;

  // ONE rule, and it names no product hostname: easysoft.ro is the company's own infrastructure
  // domain, where non-production environments live. Everything else on a real domain is a customer
  // looking at the real thing.
  //
  // Stated this way so renaming a product host — or standing up a new environment — needs no change
  // here. The previous version listed hostnames, which meant a deploy to a new domain silently
  // resolved to no environment and dropped every hit: the dashboard still loads, it just reads zero,
  // and that is the hardest kind of analytics failure to notice.
  return hostname === 'easysoft.ro' || hostname.endsWith('.easysoft.ro') ? 'staging' : 'production';
}

/**
 * The analytics config for this app on this host, or undefined to stay switched off.
 *
 * Pass the result straight to `initAnalytics` — it already treats undefined as "do nothing", and
 * still gates the script behind the visitor's consent regardless of what this returns.
 */
export function resolveAnalytics(app: AnalyticsApp): UmamiConfig | undefined {
  // A local override, so a developer can point at their own Umami without editing this table.
  //
  // Read through a cast because this package compiles with plain `tsc` and has no Vite client
  // types — `import.meta.env` is substituted by the consuming app's build, not by ours. A global
  // type declaration here would be this package claiming a bundler it does not have.
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const envUrl = env?.VITE_UMAMI_URL;
  const envId = env?.VITE_UMAMI_WEBSITE_ID;
  if (envUrl && envId) return { url: envUrl, websiteId: envId };

  if (typeof window === 'undefined') return undefined;

  const environment = environmentFor(window.location.hostname);
  if (!environment) return undefined;

  const websiteId = WEBSITE_IDS[environment][app];
  return websiteId ? { url: UMAMI_URL, websiteId } : undefined;
}
